import React, { useState, useEffect, useRef, useCallback } from "react";
import { Modal, Table, Pagination } from "react-bootstrap";
import { ModalSkeleton } from "../../../components/Dashboard/SkeletonLoaders";
import { getInsuranceLandlordDetails } from "../../../context/functions/AdminFunction";
import { toast } from "react-toastify";
import moment from "moment";
import { X } from "lucide-react";
import { constants } from "../../../utils/constants";
import { CDN_ENDPOINT, CDN_CONTAINER } from "../../../config";

const LIMIT = constants.LISTING_ROWLIMIT;

const InsuranceDetailsModal = ({ show, onHide, selectedLandlord }) => {
  const [workspaceFilter, setWorkspaceFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [paymentFilter, setPaymentFilter] = useState("all");

  const [insuranceList, setInsuranceList] = useState([]);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);

  const [workspaceOptions, setWorkspaceOptions] = useState([]);
  const [paymentMethodOptions, setPaymentMethodOptions] = useState([]);

  const abortRef = useRef(null);

  const fetchDetails = useCallback(
    async ({ workspaceId, status, payment_method, page }) => {
      const landlordId = selectedLandlord?.landlord_id || selectedLandlord?.landlord?._id;
      if (!landlordId) return;

      if (abortRef.current) abortRef.current.abort();
      abortRef.current = new AbortController();

      setLoading(true);
      try {
        const res = await getInsuranceLandlordDetails({
          landlordId,
          workspaceId: workspaceId === "all" ? "" : workspaceId,
          status,
          payment_method: payment_method === "all" ? "" : payment_method,
          page,
          limit: LIMIT,
          signal: abortRef.current.signal,
        });

        if (!res || res.status === "canceled") return;

        if (res.success && res.success_res) {
          setInsuranceList(res.success_res.data || []);
          setTotal(res.success_res.total || 0);
          if (res.success_res.workspaces?.length) setWorkspaceOptions(res.success_res.workspaces);
          if (res.success_res.paymentMethods?.length) setPaymentMethodOptions(res.success_res.paymentMethods);
        } else {
          setInsuranceList([]);
          setTotal(0);
        }
      } catch (err) {
        if (err.name !== "CanceledError" && err.code !== "ERR_CANCELED") {
          toast.error("Failed to load insurance details");
        }
      } finally {
        setLoading(false);
      }
    },
    [selectedLandlord]
  );

  useEffect(() => {
    if (!show) return;
    fetchDetails({
      workspaceId: workspaceFilter,
      status: statusFilter,
      payment_method: paymentFilter,
      page: currentPage,
    });
  }, [show, workspaceFilter, statusFilter, paymentFilter, currentPage, fetchDetails]);

  useEffect(() => {
    if (show) {
      setWorkspaceFilter("all");
      setStatusFilter("all");
      setPaymentFilter("all");
      setCurrentPage(1);
      setInsuranceList([]);
      setWorkspaceOptions([]);
      setPaymentMethodOptions([]);
    }
  }, [show, selectedLandlord?.landlord_id]);

  useEffect(() => {
    return () => { if (abortRef.current) abortRef.current.abort(); };
  }, []);

  const handleFilterChange = (type, value) => {
    setCurrentPage(1);
    if (type === "workspace") setWorkspaceFilter(value);
    if (type === "status") setStatusFilter(value);
    if (type === "payment") setPaymentFilter(value);
  };

  const handleHide = () => {
    if (abortRef.current) abortRef.current.abort();
    onHide();
  };

  const totalPages = Math.ceil(total / LIMIT);

  const renderPagination = () => {
    const items = [];
    const maxPagesToShow = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);
    if (endPage - startPage + 1 < maxPagesToShow) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }
    items.push(<Pagination.First key="first" onClick={() => setCurrentPage(1)} disabled={currentPage === 1} />);
    items.push(<Pagination.Prev key="prev" onClick={() => setCurrentPage((p) => p - 1)} disabled={currentPage === 1} />);
    for (let p = startPage; p <= endPage; p++) {
      items.push(
        <Pagination.Item key={p} active={p === currentPage} onClick={() => setCurrentPage(p)}>{p}</Pagination.Item>
      );
    }
    items.push(<Pagination.Next key="next" onClick={() => setCurrentPage((p) => p + 1)} disabled={currentPage === totalPages || totalPages === 0} />);
    items.push(<Pagination.Last key="last" onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages || totalPages === 0} />);

    return (
      <div className="d-flex justify-content-between align-items-center mt-4">
        <div className="text-muted small">
          Showing <strong>{total > 0 ? (currentPage - 1) * LIMIT + 1 : 0}</strong> to{" "}
          <strong>{Math.min(currentPage * LIMIT, total)}</strong> of{" "}
          <strong>{total}</strong> records
        </div>
        <Pagination className="tenants-pagination mb-0">{items}</Pagination>
      </div>
    );
  };

  const formatProperty = (property) => {
    if (!property) return "N/A";
    return [property.street, property.street_number, property.muncipality]
      .filter(Boolean)
      .join(", ") || "N/A";
  };

  const formatPaymentLabel = (method) => {
    if (!method) return "N/A";
    if (method === "direct_debit") return "Direct debit";
    return method.charAt(0).toUpperCase() + method.slice(1);
  };

  const parsePolicyDate = (dateStr) => {
    if (!dateStr) return null;
    // Handles "DD/MM/YYYY" and ISO formats
    if (dateStr.includes("/")) return moment(dateStr, "DD/MM/YYYY");
    return moment(dateStr);
  };

  return (
    <Modal
      show={show}
      onHide={handleHide}
      size="xl"
      dialogClassName="modal-90w"
      centered
      scrollable
      className="landlord-metrics-modal"
    >
      <Modal.Header className="landlord-details-header">
        <div className="d-flex justify-content-between align-items-start w-100">
          <div>
            <h4 className="landlord-details-name">
              {selectedLandlord?.landlord?.fname_companyName}{" "}
              {selectedLandlord?.landlord?.lname}
            </h4>
            <p className="landlord-details-email">
              {selectedLandlord?.landlord?.email || "No email provided"}
            </p>
          </div>
          <button className="modal-close-btn" onClick={handleHide}>
            <X size={20} />
          </button>
        </div>
      </Modal.Header>

      <Modal.Body className="px-5 pb-5 pt-0">
        {/* Workspace Selector */}
        <div className="workspace-select-container mt-4 mb-4">
          <select
            className="workspace-select"
            value={workspaceFilter}
            onChange={(e) => handleFilterChange("workspace", e.target.value)}
          >
            <option value="all">All workspaces</option>
            {workspaceOptions.map((ws) => (
              <option key={ws._id} value={ws._id}>{ws.name}</option>
            ))}
          </select>
        </div>

        {/* Filter Pills */}
        <div className="filter-pills-row mb-4">
          <div className="filter-pill-group">
            <span className="filter-pill-label">Status</span>
            <div className="filter-pill-options">
              {["all", "active", "expired"].map((s) => (
                <div
                  key={s}
                  className={`modal-filter-pill ${statusFilter === s ? "active" : ""}`}
                  onClick={() => handleFilterChange("status", s)}
                >
                  {s.charAt(0).toUpperCase() + s.slice(1)}
                </div>
              ))}
            </div>
          </div>

          <div className="filter-pill-group">
            <span className="filter-pill-label">Payment</span>
            <div className="filter-pill-options">
              <div
                className={`modal-filter-pill ${paymentFilter === "all" ? "active" : ""}`}
                onClick={() => handleFilterChange("payment", "all")}
              >
                All
              </div>
              {paymentMethodOptions.map((method) => (
                <div
                  key={method}
                  className={`modal-filter-pill ${paymentFilter === method ? "active" : ""}`}
                  onClick={() => handleFilterChange("payment", method)}
                >
                  {formatPaymentLabel(method)}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Table */}
        {loading ? (
          <ModalSkeleton />
        ) : (
          <>
            <div className="table-responsive">
              <Table hover className="landlord-modal-table mb-0">
                <thead>
                  <tr>
                    <th>COVERED PROPERTY</th>
                    <th>BENEFICIARIES</th>
                    <th>WORKSPACES</th>
                    <th>POLICY START</th>
                    <th>POLICY END</th>
                    <th>PREMIUM</th>
                    <th>PAYMENT</th>
                    <th>POLICY DOC</th>
                    <th>INVOICE</th>
                    <th>STATUS</th>
                  </tr>
                </thead>
                <tbody>
                  {insuranceList.length > 0 ? (
                    insuranceList.map((item, index) => {
                      const isExpired = item.insurance_status === "expired";
                      const isDirectDebit = item.payment_method === "direct_debit";
                      const policyDate = parsePolicyDate(item.policy_start);

                      const beneficiaryNames = (item.tenants || [])
                        .map((t) => `${t.fname || ""} ${t.lname || ""}`.trim())
                        .filter(Boolean)
                        .join(", ");

                      const workspaceNames = (item.workspaces || (item.workspace ? [item.workspace] : []))
                        .map((ws) => ws.name)
                        .filter(Boolean)
                        .join(", ");

                      return (
                        <tr key={item._id || index}>
                          <td className="fw-600">{formatProperty(item.property)}</td>
                          <td style={{ maxWidth: "140px" }}>
                            <div
                              className="fw-600"
                              style={{
                                fontSize: "0.8rem",
                                lineHeight: "1.3",
                                wordBreak: "break-word",
                              }}
                            >
                              {beneficiaryNames || "N/A"}
                            </div>
                          </td>
                          <td style={{ maxWidth: "140px" }}>
                            <div
                              className="fw-600"
                              style={{
                                fontSize: "0.8rem",
                                lineHeight: "1.3",
                                wordBreak: "break-word",
                              }}
                            >
                              {workspaceNames || "N/A"}
                            </div>
                          </td>
                          <td className="text-muted small fw-medium">
                            {policyDate?.isValid() ? policyDate.format("DD MMM YYYY") : "N/A"}
                          </td>
                          <td className="text-muted small fw-medium">
                            {item.policy_end
                              ? moment(item.policy_end).format("DD MMM YYYY")
                              : "—"}
                          </td>
                          <td className="fw-600">
                            {item.payment_amount ? `€${item.payment_amount}/año` : "N/A"}
                          </td>
                          <td>
                            {item.payment_method ? (
                              <span className={`insurance-payment-badge ${isDirectDebit ? "direct-debit" : "card"}`}>
                                {isDirectDebit ? "Direct debit" : formatPaymentLabel(item.payment_method)}
                              </span>
                            ) : (
                              <span className="text-muted">—</span>
                            )}
                          </td>
                          <td>
                            {item.policy_document ? (
                              <a
                                href={`${CDN_ENDPOINT}/${CDN_CONTAINER}/${item.policy_document}`}
                                target="_blank"
                                rel="noreferrer"
                                className="insurance-doc-link"
                              >
                                View
                              </a>
                            ) : (
                              <span className="text-muted">—</span>
                            )}
                          </td>
                          <td>
                            {item.invoice_document ? (
                              <a
                                href={`${CDN_ENDPOINT}/${CDN_CONTAINER}/${item.invoice_document}`}
                                target="_blank"
                                rel="noreferrer"
                                className="insurance-doc-link"
                              >
                                View
                              </a>
                            ) : (
                              <span className="text-muted">—</span>
                            )}
                          </td>
                          <td>
                            <span className={isExpired ? "status-badge-expired" : "status-badge-active"}>
                              {isExpired ? "Expired" : "Active"}
                            </span>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={10} className="text-center py-5 text-muted">
                        No results found matching your filters
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
            {totalPages > 1 && total > 0 && (
              <div className="px-1">{renderPagination()}</div>
            )}
          </>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default InsuranceDetailsModal;
